import { useLayoutEffect, useRef, type RefObject } from 'react';
import { practiceNow } from '@/shared/lib/practice-time';

// Opens a time grid on now (design log 15 Oct, calendar clean-up: "grids open on now"). The Signal DS Week, By doctor
// and By room grids draw the whole day from their first hour and open there (00:00 on a day with early bookings), so
// the now line and today's bookings sit out of view. The DS page's grid area is the one scroller; this puts the
// current time in the upper third of it.

/** Where the scroller must be so `nowMinutes` sits in the upper third of a viewport `clientHeight` tall. Never negative. */
export function scrollTopForNow({
  nowMinutes,
  startHour,
  endHour,
  hourHeight,
  clientHeight,
}: {
  nowMinutes: number;
  startHour: number;
  endHour: number;
  hourHeight: number;
  clientHeight: number;
}): number {
  const mins = Math.min(Math.max(nowMinutes, startHour * 60), endHour * 60);
  return Math.max(0, ((mins - startHour * 60) / 60) * hourHeight - clientHeight / 3);
}

/**
 * The element that scrolls the grid: the page's grid area when it can scroll, else the first inner scroller of the
 * view (the bridge pins the area and lets the view's own body scroll, design log 15 Oct "header and gutter pinned").
 */
function scrollerFor(area: HTMLElement): HTMLElement | null {
  if (area.scrollHeight > area.clientHeight) return area;
  for (const el of area.querySelectorAll<HTMLElement>('div')) {
    const overflowY = getComputedStyle(el).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) return el;
  }
  return null;
}

/**
 * `anchor` is any element inside the DS page's grid area; the scroller is the grid area around it, or the view's own
 * body when the area is pinned. It fires once per
 * (key | first hour | last hour), and only once the area can scroll: the grids first draw a loader or a default range,
 * so a one-shot latch on mount would fire too early. It never fights the user's own scrolling afterwards.
 */
export function useScrollToNow(
  anchor: RefObject<HTMLElement | null>,
  { enabled, startHour, endHour, hourHeight, key }: { enabled: boolean; startHour: number; endHour: number; hourHeight: number; key: string },
): void {
  const done = useRef('');
  useLayoutEffect(() => {
    if (!enabled) return;
    const el = anchor.current?.closest<HTMLElement>('[data-slot="pa-calendar-page-grid"]');
    if (!el) return;
    const k = `${key}|${startHour}|${endHour}`;
    if (done.current === k) return;
    const scroller = scrollerFor(el);
    if (!scroller) return;
    const now = practiceNow();
    scroller.scrollTop = scrollTopForNow({
      nowMinutes: now.getHours() * 60 + now.getMinutes(),
      startHour,
      endHour,
      hourHeight,
      clientHeight: scroller.clientHeight,
    });
    done.current = k;
  });
}
